import { useEffect, useMemo, useState } from "react";
import { X, ChevronLeft, ChevronRight, Image as ImageIcon } from "lucide-react";
import { api, wpImg } from "@/lib/api";

export default function Gallery() {
  const [cats, setCats] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [activeCat, setActiveCat] = useState(null);

  useEffect(() => {
    api.get("/gallery-categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/gallery-photos").then((r) => setPhotos(r.data)).catch(() => {});
  }, []);

  const visibleCats = useMemo(() => {
    // Only show categories that actually have photos
    const withCounts = cats.map((c) => ({ ...c, count: photos.filter((p) => p.category === c.name).length }));
    return withCounts.filter((c) => c.count > 0);
  }, [cats, photos]);

  const filteredPhotos = useMemo(() => {
    const list = activeCat ? photos.filter((p) => p.category === activeCat) : photos;
    return list.sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [photos, activeCat]);

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const nextPhoto = () => setLightboxIndex((i) => (i + 1) % filteredPhotos.length);
  const prevPhoto = () => setLightboxIndex((i) => (i - 1 + filteredPhotos.length) % filteredPhotos.length);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextPhoto();
      if (e.key === "ArrowLeft") prevPhoto();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [lightboxIndex, filteredPhotos.length]);

  return (
    <div data-testid="gallery-page">
      <section className="relative py-20 md:py-28 overflow-hidden border-b border-sand">
        <div className="absolute inset-0 pattern-girih opacity-25" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-[11px] uppercase tracking-[0.25em] text-gold font-semibold flex items-center gap-2">
            <ImageIcon size={12} /> Photo Gallery
          </div>
          <h1 className="display text-4xl md:text-6xl mt-3 text-ink">Life at Flint Islamic Center</h1>
          <p className="mt-4 max-w-2xl text-ink/65 font-light">
            Moments from our interfaith gatherings, Eid celebrations, family camps, and community events.
            Click any photo to enlarge.
          </p>
        </div>
      </section>

      <section className="py-10 md:py-14 px-6 md:px-10">
        <div className="max-w-6xl mx-auto">
          {/* category filter */}
          {visibleCats.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-10">
              <FilterChip active={activeCat === null} label={`All (${photos.length})`} onClick={() => setActiveCat(null)} testId="gal-cat-all" />
              {visibleCats.map((c) => (
                <FilterChip key={c.id} active={activeCat === c.name} label={`${c.name} (${c.count})`} onClick={() => setActiveCat(c.name)} testId={`gal-cat-${c.name.toLowerCase().replace(/\s+/g, "-")}`} />
              ))}
            </div>
          )}

          {photos.length === 0 ? (
            <div className="py-24 text-center text-ink/50 italic">
              Photos will appear here once the admin uploads them.
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
              {filteredPhotos.map((p, i) => (
                <button
                  key={p.id || p.image_url + i}
                  onClick={() => openLightbox(i)}
                  data-testid={`gallery-photo-${i}`}
                  className="relative mb-4 block w-full overflow-hidden rounded-2xl group break-inside-avoid"
                >
                  <img
                    src={wpImg(p.image_url)}
                    alt={p.caption || p.category}
                    loading="lazy"
                    className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                    <div className="text-cream text-sm">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{p.category}</div>
                      {p.caption && <div className="mt-1 font-light">{p.caption}</div>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {lightboxIndex !== null && filteredPhotos[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[110] bg-navy/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          onClick={closeLightbox}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            data-testid="lightbox-close"
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-cream/10 hover:bg-cream/20 text-cream flex items-center justify-center"
          ><X size={22} /></button>

          {filteredPhotos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                data-testid="lightbox-prev"
                className="absolute left-3 md:left-10 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-cream/10 hover:bg-cream/20 text-cream flex items-center justify-center"
              ><ChevronLeft size={24} /></button>
              <button
                onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                data-testid="lightbox-next"
                className="absolute right-3 md:right-10 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-cream/10 hover:bg-cream/20 text-cream flex items-center justify-center"
              ><ChevronRight size={24} /></button>
            </>
          )}

          <figure className="relative max-h-[90vh] max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={filteredPhotos[lightboxIndex].image_url}
              alt={filteredPhotos[lightboxIndex].caption || ""}
              className="max-h-[80vh] w-auto rounded-2xl shadow-2xl"
            />
            <figcaption className="mt-4 text-center text-cream/75 text-sm">
              <span className="text-gold text-xs uppercase tracking-[0.2em] mr-2">{filteredPhotos[lightboxIndex].category}</span>
              {filteredPhotos[lightboxIndex].caption}
              <span className="ml-3 text-cream/50 text-xs">· {lightboxIndex + 1}/{filteredPhotos.length}</span>
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
        active ? "bg-navy text-cream" : "bg-cream border border-sand text-ink/70 hover:bg-sand/50"
      }`}
    >
      {label}
    </button>
  );
}
